
import { watch, onBeforeMount } from "vue";
import axios from "@/lib/axios";
import { useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { useDebounceFn } from "@vueuse/core";
import { useCorporationsSearchStore } from "@/stores/corporationsSearch";
import { Corporation } from "@/interfaces/corporation";
import { useQueryClient } from "@tanstack/vue-query";

interface Props {
  immediate?: boolean;
}

const getCorporations = async (params: any): Promise<Corporation[]> => {
  const { data } = await axios.get<Corporation[]>(`/api/corporations/search`, {
    params: {
      ...params,
      ...(params.q && { q: params.q.toLowerCase().trim() }),
    },
  });
  return data;
};

const useCorporationsSearch = (props?: Props) => {
  const { immediate = false } = props || {};
  const { setQuery, setCorporations, resetState } =
    useCorporationsSearchStore();
  const { query, corporations } = storeToRefs(useCorporationsSearchStore());
  const queryClient = useQueryClient();

  const reset = () => {
    queryClient.getQueryCache().find(["corporationsSearch"])?.reset();
    resetState();
  };

  onBeforeMount(() => {
    reset();
    if (immediate) {
      refetch();
    }
  });

  const { data, isLoading, refetch, isError, error } = useQuery(
    ["corporationsSearch"],
    () => getCorporations({ q: query.value || undefined }),
    {
      enabled: immediate,
      retry: false,
    }
  );

  watch(query, () => {
    searchCorporations();
  });

  watch(data, () => {
    if (data.value) {
      setCorporations(data.value);
    }
  });

  const searchCorporations = useDebounceFn(() => {
    if (query.value.length) {
      refetch();
    }
  }, 500);

  return {
    query,
    corporations,
    data,
    isLoading,
    isError,
    error,
    refetch,
    setQuery,
    setCorporations,
  };
};

export default useCorporationsSearch;
